import React, {Fragment} from 'react';

import {useSnackbar} from 'notistack';
import {CallMade} from '@material-ui/icons';
import {Box, Typography} from '@material-ui/core';

import {
    AnalyticsCategories,
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
    StatusRegisterMessages,
    StatusTitles,
} from 'commons/constants';
import {
    EfeResponse,
    ErrorResponse,
    ExpedientResponse,
    SyncClientDataResponse,
} from 'commons/models';
import {Utils} from 'commons';
import {ClientService} from 'services';
import {useAlert, useSpinner} from 'hooks';
import {MainContent} from 'layouts/commons';
import {messageFunctions} from 'commons/utils';
import {useAnalytics} from 'hooks/AnalyticsHook';
import {EfeButtonPrimary, EfeSave} from 'components';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';

function AuthClient() {
    const {enqueueSnackbar} = useSnackbar();
    const {sendPageView, sendEvent} = useAnalytics();
    const {flow, updateModuleStep} = useFlowContext();
    const {fatherKycId, expedientId, menu} = useMenuContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [error, setError] = React.useState<Array<string>>([]);
    const [showData, setShowData] = React.useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [clientStatus, setClientStatus] = React.useState<string>();
    const [userBlocked, setUserBlocked] = React.useState<boolean>(true);
    const [message, setMessage] = React.useState<string>(StatusRegisterMessages.PROCESS_OK);

    async function getExpedientStatus(): Promise<string> {
        let state = '-1';
        try {
            const response: EfeResponse<ExpedientResponse> = await ClientService.getExpedientStatus(
                expedientId
            );
            if (response && response.data) {
                const data: ExpedientResponse = response.data;
                state = data.stateKey;
            } else {
                setMessage(Messages.COMMON_ERROR);
            }
        } catch (error) {
            setMessage(Messages.COMMON_ERROR);
        }
        return state;
    }

    async function sendToAml() {
        let message = messageFunctions.getAnalyticsMessage(
            AnalyticsCategories.SEND,
            'Onboarding KYC Error.'
        );
        try {
            setIsLoading(true);

            const response: EfeResponse<SyncClientDataResponse> =
                await ClientService.syncClientData(expedientId.toString());
            if (response && response.data) {
                message = messageFunctions.getAnalyticsMessage(
                    `${AnalyticsCategories.SEND} to`,
                    AnalyticsCategories.AML
                );
                enqueueSnackbar(Messages.SUCCESSFUL_SYNC_DATA, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const status = await getExpedientStatus();
                setClientStatus(status);
                if (Constants.AML_AUTHORIZED_ID === status) {
                    setUserBlocked(false);
                    setMessage(StatusRegisterMessages.PROCESS_OK);
                } else {
                    setMessage(StatusRegisterMessages.CONTRACT_IN_REVISION);
                }
            }
            setShowData(true);
            setIsLoading(false);
        } catch (error) {
            setShowData(true);
            if (Utils.functions.isCustomErrorService(error)) {
                const errorData: ErrorResponse = (error as any).data;
                setMessage(errorData.message);
                setError(errorData.errors || []);
            } else {
                setMessage(Messages.COMMON_ERROR);
            }
            setUserBlocked(true);
            setIsLoading(false);
            showAlertError(error, Messages.COMMON_ERROR);
        }
        sendEvent(AnalyticsCategories.AML, message);
    }

    React.useEffect(() => {
        sendPageView(HistoryRoutes.COMPLETED_REGISTER);
        if (fatherKycId > 0 && expedientId > 0) {
            (async function () {
                setIsLoading(true);
                const status = await getExpedientStatus();
                setClientStatus(status);
                if (Constants.AML_ONBOARDING_ID === status) {
                    setUserBlocked(true);
                    setShowData(false);
                } else if (Constants.AML_AUTHORIZED_ID === status) {
                    setUserBlocked(false);
                    setShowData(true);
                    setMessage(StatusRegisterMessages.PROCESS_OK);
                } else {
                    setUserBlocked(true);
                    setShowData(true);
                    setMessage(StatusRegisterMessages.CONTRACT_IN_REVISION);
                }
                setIsLoading(false);
            })();
        } else {
            setShowData(true);
        }
    }, []);

    const Title = (
        <Box m={4} mb={2}>
            <Typography component={'span'} variant='h3'>
                {userBlocked ? StatusTitles.AVISO : StatusTitles.LISTO}
            </Typography>
        </Box>
    );

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <MainContent>
                {showData && Title}
                {showData && (
                    <Box m={4} mb={8}>
                        <Typography component={'span'} variant='h4'>
                            {message}
                        </Typography>
                        {error && (
                            <Typography
                                variant='h5'
                                display='block'
                                gutterBottom
                                component={'span'}>
                                <ul className='pl-3'>
                                    {error
                                        .filter((item) => item)
                                        .map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                </ul>
                            </Typography>
                        )}
                    </Box>
                )}
                <Box m={4} mb={8}>
                    {!showData && (
                        <Box m={4} mb={2}>
                            <Typography component={'span'} variant='h5'>
                                Favor de finalizar el proceso dando click en el bot&oacute;n
                                &quot;Enviar Datos a Autorizaci&oacute;n&quot;
                            </Typography>
                        </Box>
                    )}

                    <Box m={4} mb={2}>
                        <EfeButtonPrimary
                            onClick={sendToAml}
                            disabled={Constants.AML_AUTHORIZED_ID === clientStatus}
                            endIcon={<CallMade />}>
                            Enviar Datos a Autorizaci&oacute;n
                        </EfeButtonPrimary>
                    </Box>
                </Box>
            </MainContent>

            <EfeSave
                flow={flow}
                menu={menu}
                disabled={Constants.AML_ONBOARDING_ID === clientStatus}
                updateModuleStep={updateModuleStep}
            />
        </Fragment>
    );
}

export default AuthClient;
